// apps/landing-page/src/app/error.tsx
'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import * as Sentry from '@sentry/nextjs';
import { recoverFromChunkError } from '@/lib/chunk-reload';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations('errors.error');

  useEffect(() => {
    // A stale chunk after a deploy isn't an application fault — reload once and the user
    // lands on the current build instead of reading an error page. Recovering first also
    // keeps deploy churn out of the issue stream.
    if (recoverFromChunkError(error)) return;
    console.error(error);
    // Route-level errors land here rather than in global-error.tsx. This app produced
    // the bulk of the reported #418 hydration noise, so it is also the one where a
    // missing report costs the most.
    Sentry.captureException(error);
  }, [error]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f7fafa] px-6">
      <div className="max-w-md text-center">
        <p className="font-mono text-sm font-medium tracking-widest text-orange-500">
          {t('code')}
        </p>
        <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900">
          {t('title')}
        </h1>
        <p className="mt-3 text-slate-600">
          {t('description')}
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            onClick={reset}
            className="inline-flex items-center justify-center rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
          >
            {t('retry')}
          </button>
          <Link
            href="/"
            className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
          >
            {t('home')}
          </Link>
        </div>
      </div>
    </main>
  );
}
